import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { api, adminHeaders } from "@/lib/api";
import { VariantEditor } from "@/components/VariantEditor";
import { AddonEditor, normalizeAddons, normalizeVariants } from "@/components/AddonEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

export const OptionSetsTab = ({ optionSets, products, onChanged }) => {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const usage = (id) => products.filter((p) => p.option_set_id === id).length;

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name is required.");
    setSaving(true);
    const payload = {
      name: editing.name.trim(),
      variants: normalizeVariants(editing.variants),
      addons: normalizeAddons(editing.addons),
    };
    try {
      if (editing.id) await api.put(`/admin/option-sets/${editing.id}`, payload, adminHeaders());
      else await api.post("/admin/option-sets", payload, adminHeaders());
      toast.success("Option set saved.");
      setEditing(null);
      onChanged();
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete option set "${s.name}"? Products using it will lose these options.`)) return;
    try {
      await api.delete(`/admin/option-sets/${s.id}`, adminHeaders());
      toast.success("Deleted.");
      onChanged();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Shared upgrade dropdowns and add-on checklists. Assign a set to many models so you only maintain
          prices once.
        </p>
        <Button
          data-testid="add-option-set-button"
          onClick={() => setEditing({ name: "", variants: [], addons: [] })}
          className="rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
        >
          <Plus size={17} className="mr-1.5" /> New Set
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {optionSets.map((s) => (
          <div key={s.id} data-testid={`option-set-${s.id}`} className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4">
            <div>
              <p className="font-display font-bold">{s.name}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {s.variants.length} upgrade groups · {s.addons.length} add-ons · used by {usage(s.id)} product
                {usage(s.id) === 1 ? "" : "s"}
              </p>
            </div>
            <div className="flex gap-1">
              <button
                data-testid={`edit-option-set-${s.id}`}
                onClick={() => setEditing({ ...s })}
                className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-secondary"
              >
                <Pencil size={15} />
              </button>
              <button
                data-testid={`delete-option-set-${s.id}`}
                onClick={() => remove(s)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-[#DC2626] hover:bg-secondary"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
        {optionSets.length === 0 && (
          <p className="col-span-full py-10 text-center text-muted-foreground">No option sets yet.</p>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editing?.id ? "Edit Option Set" : "New Option Set"}</DialogTitle>
            <DialogDescription>Upgrades are single-choice dropdowns; add-ons are a multi-select checklist.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-6">
              <div>
                <Label>Set name</Label>
                <Input
                  data-testid="option-set-name-input"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Upgrades (dropdowns)</Label>
                <div className="mt-1.5">
                  <VariantEditor value={editing.variants} onChange={(variants) => setEditing({ ...editing, variants })} />
                </div>
              </div>
              <div>
                <Label>Add-ons (checklist)</Label>
                <div className="mt-1.5">
                  <AddonEditor value={editing.addons} onChange={(addons) => setEditing({ ...editing, addons })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              data-testid="save-option-set-button"
              onClick={save}
              disabled={saving}
              className="rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
